import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { downloadSubmissionDataApi, updateSubmissionDataApi } from '../../api/submissions-database';
import { useUserDetialsContext } from '../../context/user-details';
import downloadDocument from '../../utils/downloadDocument';
import { ArchiveIcon, DeleteIcon, DownloadIcon, ProcessingIconDashed, UnarchiveIcon } from '../icons/icons';
import DocPermissionEnum from 'enums/DocPermissionEnum';
import { AnimatedTextWrapper } from 'components/atoms/AnimatedDotsText/AnimatedDotsTextStyles';

function RenderActionIcons({
  item,
  setSubmissionData,
  isArchiveTable,
  setConfirmationModal,
  canEdit = false
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const itemStatus = item?.status?.toLowerCase();
  const {
    userDetails: { isIndividualUser }
  } = useUserDetialsContext();
  const [isDownloading, setIsDownloading] = useState(false);
  const isOwner = item?.actions?.includes(DocPermissionEnum.DOC_OWN);
  const isSharingEnabled = !isIndividualUser;
  const updatedSubmissionArchiveStatus = async (value) => {
    setSubmissionData((prevData) =>
      prevData.filter((submission) => {
        return submission._id !== item._id;
      })
    );
    updateSubmissionDataApi({ data: { _id: item._id, isArchived: Boolean(value) } });
  };

const downloadSubmission = async () => {
  const reportTitle = item?.title || '';
  setIsDownloading(true);

  const toastId = toast.loading(
    <AnimatedTextWrapper>'{reportTitle}' report downloading</AnimatedTextWrapper>,
    { duration: Infinity }
  );
  try {
    const response = await downloadSubmissionDataApi({ submissionId: item._id });
    const { filename } = response.headers;
    downloadDocument(response.data, filename);
    toast.success(`'${reportTitle}' report downloaded.`, { id: toastId, duration: 3000 });
  } catch (error) {
    toast.error(`'${reportTitle}' report Failed to download file.`, {
      id: toastId,
      duration: 3000
    });
  } finally {
    setIsDownloading(false);
  }
};

const downloadIcon = () =>
  item?.actions?.includes(DocPermissionEnum.DOC_DOWNLOAD) &&
  (isDownloading ? (
     <ProcessingIconDashed className="spin" />
  ) : (
    <DownloadIcon
      className="hover:stroke-primary transition-all duration-300"
      onClick={downloadSubmission}
      disabled={item?.status?.toLowerCase() !== 'completed'}
    />
  ));

  const deleteIcon = () =>
    isOwner && (
      <DeleteIcon
        onClick={() => setConfirmationModal(true)}
        className="hover:stroke-primary transition-all duration-300"
      />
    );

  const moreIcon = () => (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-800 transition-all duration-300 text-white"
        title="More actions">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M9 4.5V4.5075M9 9V9.0075M9 13.5V13.5075"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-1 w-32 bg-[#1d2939] border border-slate-700 rounded-lg shadow-xl z-[100] py-1 animate-in fade-in zoom-in duration-200">
          {canEdit && (
            <button
              type="button"
              onClick={() => {
                updatedSubmissionArchiveStatus('isArchived', !item?.isArchived);
                setShowMenu(false);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-slate-700 transition-colors">
              {item?.isArchived ? (
                <>
                  <UnarchiveIcon className="!w-4 !h-4" />
                  <span>Unarchive</span>
                </>
              ) : (
                <>
                  <ArchiveIcon className="!w-4 !h-4" />
                  <span>Archive</span>
                </>
              )}
            </button>
          )}

          {isOwner && (
            <button
              type="button"
              onClick={() => {
                setConfirmationModal(true);
                setShowMenu(false);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors">
              <DeleteIcon className="!w-4 !h-4 animate-none" />
              <span>Delete</span>
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (itemStatus?.toLowerCase() === 'processing') {
    return '';
  }

  const detailIcon = () => (
    <button
      type="button"
      title="View report"
      onClick={() => navigate(`/report/${item._id}`)}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-orange-500 bg-orange-400/20 border border-orange-400/60 rounded-full hover:bg-orange-400/30 hover:border-orange-400/80 hover:text-orange-300 transition-all duration-150">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 5s1.5-3 4-3 4 3 4 3-1.5 3-4 3-4-3-4-3z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
        <circle cx="5" cy="5" r="1.3" stroke="currentColor" strokeWidth="1.1"/>
      </svg>
      View
    </button>
  );

  return (
    <div className="flex items-center justify-end gap-2 w-full pr-1">
      {detailIcon()}
      {downloadIcon()}
      {(canEdit || isOwner) && moreIcon()}
    </div>
  );
}

export default RenderActionIcons;
